import axios from 'axios';
import { API_CONFIG, getApiUrl } from '../config/apiConfig';

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  timeout: API_CONFIG.TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  async (config) => {
    try {
      const token = localStorage.getItem('authToken');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      
      // Add user ID to headers for cart operations
      const userId = localStorage.getItem('userId');
      if (userId) {
        config.headers['user-id'] = userId;
      }
      config.headers['X-Order-Channel'] = 'website';
    } catch (error) {
      console.log('Error getting stored data:', error);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Called when the server says the session is no longer valid (set by AuthContext).
let sessionExpiredHandler = null;

/** Server rejected this user's session: they must log in again with OTP. */
export function isSessionExpiredError(error) {
  return error?.response?.status === 401 && error?.response?.data?.code === 'AUTH_REQUIRED';
}

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    console.log('API Error:', error.response?.data || error.message);
    if (isSessionExpiredError(error) && sessionExpiredHandler) {
      sessionExpiredHandler();
    }
    return Promise.reject(error);
  }
);

// API Service Class
class ApiService {
  // Health Check
  async healthCheck() {
    try {
      const response = await apiClient.get(API_CONFIG.ENDPOINTS.HEALTH);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Authentication APIs
  async testAuth() {
    try {
      const response = await apiClient.get(API_CONFIG.ENDPOINTS.AUTH.TEST);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async sendOtp(mobileNumber) {
    try {
      const response = await apiClient.post(API_CONFIG.ENDPOINTS.AUTH.SEND_OTP, {
        mobileNumber,
      });
      return response.data;
    } catch (error) {
      
      if (error.response) {
        return {
          success: false,
          message: error.response.data?.message || 'Failed to send OTP. Please try again.',
        };
      } else if (error.request) {
        return {
          success: false,
          message: 'Cannot connect to server. Make sure backend is running.',
        };
      } else {
        return {
          success: false,
          message: `Failed to send OTP: ${error.message || 'Unknown error'}`,
        };
      }
    }
  }

  async loginMobile(mobileNumber, otp) {
    try {
      const response = await apiClient.post(API_CONFIG.ENDPOINTS.AUTH.LOGIN_MOBILE, {
        mobileNumber,
        otp,
      });
      
      const data = response.data || {};
      if (data.success === true && data.user) {
        await this.storeUserData(data.user);
        if (data.token) {
          localStorage.setItem('authToken', data.token);
        }
        return data;
      }
      const message = (data.message != null) ? String(data.message) : '';
      const isNewUserBackend = data.isNewUser === true || data.userNotFound === true ||
        /not found|register first/i.test(message);
      if (isNewUserBackend) {
        return {
          success: false,
          message: message || 'User not found. Please create an account.',
          userNotFound: true,
        };
      }
      return { ...data, userNotFound: false };
    } catch (error) {
      const status = error.response?.status;
      const data = error.response?.data || {};
      const message = (data.message != null) ? String(data.message) : '';
      if (status === 404 || /not found|register first/i.test(message)) {
        return {
          success: false,
          message: message || 'User not found. Please create an account.',
          userNotFound: true,
        };
      }
      throw error;
    }
  }

  async login(email, password) {
    try {
      const response = await apiClient.post(API_CONFIG.ENDPOINTS.AUTH.LOGIN, {
        email,
        password,
      });
      
      // Store user data if login successful
      if (response.data.success) {
        await this.storeUserData(response.data.user);
        // Store token if provided
        if (response.data.token) {
          localStorage.setItem('authToken', response.data.token);
        }
      }
      
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async register(userData) {
    try {
      const response = await apiClient.post(API_CONFIG.ENDPOINTS.AUTH.REGISTER, userData);
      
      // Store user data if registration successful
      if (response.data.success) {
        await this.storeUserData(response.data.user);
        // Store token if provided
        if (response.data.token) {
          localStorage.setItem('authToken', response.data.token);
        }
        // Store user ID
        if (response.data.user?.id) {
          localStorage.setItem('userId', response.data.user.id);
        }
      }
      
      return response.data;
    } catch (error) {
      if (error.response) {
        return {
          success: false,
          message: error.response.data?.message || 'Registration failed. Please try again.',
        };
      } else if (error.request) {
        return {
          success: false,
          message: 'Cannot connect to server. Make sure backend is running.',
        };
      } else {
        return {
          success: false,
          message: error.message || 'Registration failed. Please try again.',
        };
      }
    }
  }

  async registerMobile(userData) {
    try {
      const response = await apiClient.post(API_CONFIG.ENDPOINTS.AUTH.REGISTER_MOBILE, userData);
      
      // Store user data if registration successful
      if (response.data.success) {
        await this.storeUserData(response.data.user);
        // Store token if provided
        if (response.data.token) {
          localStorage.setItem('authToken', response.data.token);
        }
        // Store user ID
        if (response.data.user?.id) {
          localStorage.setItem('userId', response.data.user.id);
        }
      }
      
      return response.data;
    } catch (error) {
      if (error.response) {
        return {
          success: false,
          message: error.response.data?.message || 'Registration failed. Please try again.',
        };
      } else if (error.request) {
        return {
          success: false,
          message: 'Cannot connect to server. Make sure backend is running.',
        };
      } else {
        return {
          success: false,
          message: error.message || 'Registration failed. Please try again.',
        };
      }
    }
  }

  // Storage helpers
  async storeUserData(user) {
    try {
      // Check if localStorage is available
      if (typeof window === 'undefined' || !window.localStorage) {
        console.warn('localStorage is not available');
        return;
      }
      
      if (!user || !user.id) {
        console.warn('Invalid user data provided');
        return;
      }
      
      localStorage.setItem('userId', user.id.toString());
      localStorage.setItem('userData', JSON.stringify(user));
      localStorage.setItem('isLoggedIn', 'true');
    } catch (error) {
      // Handle quota exceeded or other storage errors
      if (error.name === 'QuotaExceededError') {
        console.error('Storage quota exceeded. Please clear some data.');
      } else {
        console.error('Error storing user data:', error);
      }
    }
  }

  async getUserData() {
    try {
      // Check if localStorage is available
      if (typeof window === 'undefined' || !window.localStorage) {
        return null;
      }
      
      const userData = localStorage.getItem('userData');
      if (!userData) {
        return null;
      }
      
      return JSON.parse(userData);
    } catch (error) {
      // Only log parsing errors, not missing data
      if (error.name === 'SyntaxError') {
        console.error('Error parsing user data:', error);
        // Clear corrupted data
        localStorage.removeItem('userData');
      }
      return null;
    }
  }

  async isLoggedIn() {
    try {
      // Check if localStorage is available
      if (typeof window === 'undefined' || !window.localStorage) {
        return false;
      }
      
      const isLoggedIn = localStorage.getItem('isLoggedIn');
      return isLoggedIn === 'true';
    } catch (error) {
      // Only log actual errors, not expected failures
      if (error && error.message) {
        console.error('Error checking login status:', error);
      }
      return false;
    }
  }

  async logout() {
    try {
      // Check if localStorage is available
      if (typeof window === 'undefined' || !window.localStorage) {
        return;
      }
      
      localStorage.removeItem('userId');
      localStorage.removeItem('userData');
      localStorage.removeItem('isLoggedIn');
      localStorage.removeItem('authToken');
    } catch (error) {
      // Log only actual errors
      if (error && error.message) {
        console.error('Error during logout:', error);
      }
    }
  }

  // Image APIs
  async getBannerImage() {
    try {
      const response = await apiClient.get(API_CONFIG.ENDPOINTS.IMAGES.GET_BANNER);
      if (response.data && response.data.success !== undefined) {
        return response.data;
      }
      // Handle case where response.data is the banner object directly
      if (response.data && response.data.imageUrl) {
        return {
          success: true,
          data: response.data,
        };
      }
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Get banner image API Error:', error.message);
      // Return null on error so app can use fallback
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch banner image',
        data: null,
      };
    }
  }

  // Schools APIs
  async searchSchools(searchTerm = '', limit = 20) {
    try {
      const response = await apiClient.get(API_CONFIG.ENDPOINTS.SCHOOLS.SEARCH, {
        params: { q: searchTerm, limit },
      });
      return response.data;
    } catch (error) {
      console.error('Search schools API Error:', error.message);
      if (error.response) {
        return {
          success: false,
          message: error.response.data?.message || 'Failed to search schools',
          data: [],
        };
      } else if (error.request) {
        return {
          success: false,
          message: 'Cannot connect to server. Make sure backend is running.',
          data: [],
        };
      } else {
        return {
          success: false,
          message: 'Failed to search schools. Please try again.',
          data: [],
        };
      }
    }
  }

  async validateSchoolCode(code) {
    try {
      const response = await apiClient.get(API_CONFIG.ENDPOINTS.SCHOOLS.VALIDATE_CODE, {
        params: { code },
      });
      return response.data;
    } catch (error) {
      console.error('Validate school code API Error:', error.message);
      if (error.response) {
        return {
          success: false,
          isValid: false,
          message: error.response.data?.message || 'Failed to validate school code',
        };
      } else if (error.request) {
        return {
          success: false,
          isValid: false,
          message: 'Cannot connect to server. Make sure backend is running.',
        };
      } else {
        return {
          success: false,
          isValid: false,
          message: 'Failed to validate school code. Please try again.',
        };
      }
    }
  }

  async getSchoolByCode(code) {
    try {
      const response = await apiClient.get(`${API_CONFIG.ENDPOINTS.SCHOOLS.GET_BY_CODE}/${code}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getSchoolById(id) {
    try {
      const response = await apiClient.get(`${API_CONFIG.ENDPOINTS.SCHOOLS.GET_BY_ID}/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getSchoolPageData(schoolId) {
    try {
      const response = await apiClient.get(`${API_CONFIG.ENDPOINTS.SCHOOLS.PAGE_DATA}/${schoolId}/page-data`);
      return response.data;
    } catch (error) {
      // Return a structured response so callers can safely fallback.
      if (error.response && error.response.status === 404) {
        return { success: false, notFound: true, message: 'Page data endpoint not found' };
      }
      return { success: false, message: error.response?.data?.message || error.message || 'Failed to load page data' };
    }
  }

  // Grades APIs
  async getGradesBySchoolId(schoolId) {
    try {
      const response = await apiClient.get(API_CONFIG.ENDPOINTS.GRADES.GET_ALL, {
        params: { schoolId },
      });
      return response.data;
    } catch (error) {
      console.error('Get grades API Error:', error.message);
      if (error.response) {
        return {
          success: false,
          message: error.response.data?.message || 'Failed to fetch grades',
          data: [],
        };
      } else if (error.request) {
        return {
          success: false,
          message: 'Cannot connect to server. Make sure backend is running.',
          data: [],
        };
      } else {
        return {
          success: false,
          message: 'Failed to fetch grades. Please try again.',
          data: [],
        };
      }
    }
  }

  async getGradeById(id) {
    try {
      const response = await apiClient.get(`${API_CONFIG.ENDPOINTS.GRADES.GET_BY_ID}/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Subgrades (sections) APIs
  async getSubgradesByGradeId(gradeId) {
    try {
      const response = await apiClient.get(API_CONFIG.ENDPOINTS.SUBGRADES.GET_ALL, {
        params: { gradeId },
      });
      return response.data;
    } catch (error) {
      console.error('Get subgrades API Error:', error.message);
      if (error.response) {
        return { success: false, message: error.response?.data?.message || 'Failed to fetch sections', data: [] };
      }
      return { success: false, message: 'Failed to fetch sections.', data: [] };
    }
  }

  async getSubgradesByGradeIds(gradeIds) {
    try {
      const response = await apiClient.post(`${API_CONFIG.ENDPOINTS.SUBGRADES.GET_ALL}/by-grade-ids`, {
        gradeIds,
      });
      return response.data;
    } catch (error) {
      console.error('Batch get subgrades API Error:', error.message);
      if (error.response) {
        return { success: false, message: error.response?.data?.message || 'Failed to fetch sections', data: [] };
      }
      return { success: false, message: 'Failed to fetch sections.', data: [] };
    }
  }

  // Categories APIs
  async getCategoriesByGradeId(gradeId) {
    try {
      const response = await apiClient.get(API_CONFIG.ENDPOINTS.CATEGORIES.GET_ALL, {
        params: { gradeId },
      });
      return response.data;
    } catch (error) {
      console.error('Get categories API Error:', error.message);
      if (error.response) {
        return {
          success: false,
          message: error.response.data?.message || 'Failed to fetch categories',
          data: [],
        };
      } else if (error.request) {
        return {
          success: false,
          message: 'Cannot connect to server. Make sure backend is running.',
          data: [],
        };
      } else {
        return {
          success: false,
          message: 'Failed to fetch categories. Please try again.',
          data: [],
        };
      }
    }
  }

  async getCategoriesBySubgradeId(subgradeId) {
    try {
      const response = await apiClient.get(API_CONFIG.ENDPOINTS.CATEGORIES.GET_ALL, {
        params: { subgradeId },
      });
      return response.data;
    } catch (error) {
      console.error('Get categories by subgrade API Error:', error.message);
      if (error.response) {
        return { success: false, message: error.response?.data?.message || 'Failed to fetch categories', data: [] };
      }
      return { success: false, message: 'Failed to fetch categories.', data: [] };
    }
  }

  // Books APIs
  async getAllBooks({ offset = 0, limit = 100 } = {}) {
    try {
      const response = await apiClient.get(API_CONFIG.ENDPOINTS.BOOKS.GET_ALL, {
        params: { offset, limit },
      });
      return response.data;
    } catch (error) {
      console.error('Get all books API Error:', error.message);
      if (error.response) {
        return {
          success: false,
          message: error.response.data?.message || 'Failed to fetch books',
          data: [],
        };
      } else if (error.request) {
        return {
          success: false,
          message: 'Cannot connect to server. Make sure backend is running.',
          data: [],
        };
      } else {
        return {
          success: false,
          message: 'Failed to fetch books. Please try again.',
          data: [],
        };
      }
    }
  }

  // categoryIds omitted = the server looks up the grade's/section's categories itself (saves a round-trip)
  async getBooksForGradePage({ schoolId, gradeId, subgradeId, categoryIds }) {
    try {
      const response = await apiClient.post(API_CONFIG.ENDPOINTS.BOOKS.GRADE_BOOKS, {
        schoolId,
        gradeId,
        ...(subgradeId ? { subgradeId } : {}),
        ...(Array.isArray(categoryIds) ? { categoryIds } : {}),
      });
      return response.data;
    } catch (error) {
      console.error('Get books for grade page API Error:', error.message);
      if (error.response) {
        return {
          success: false,
          message: error.response.data?.message || 'Failed to fetch books',
          data: [],
        };
      }
      if (error.request) {
        return {
          success: false,
          message: 'Cannot connect to server. Make sure backend is running.',
          data: [],
        };
      }
      return { success: false, message: error.message || 'Failed to fetch books.', data: [] };
    }
  }

  /**
   * Minimal payload: which gradeIds and subgradeIds have active books for this school.
   */
  async getSchoolBookPresence(schoolId) {
    try {
      const response = await apiClient.get(API_CONFIG.ENDPOINTS.BOOKS.SCHOOL_BOOK_PRESENCE, {
        params: { schoolId },
      });
      return response.data;
    } catch (error) {
      console.error('Get school book presence API Error:', error.message);
      if (error.response) {
        return {
          success: false,
          message: error.response.data?.message || 'Failed to fetch book presence',
          data: null,
        };
      } else if (error.request) {
        return {
          success: false,
          message: 'Cannot connect to server. Make sure backend is running.',
          data: null,
        };
      }
      return {
        success: false,
        message: 'Failed to fetch book presence. Please try again.',
        data: null,
      };
    }
  }

  // Cart APIs
  async getCartCount() {
    try {
      const response = await apiClient.get(API_CONFIG.ENDPOINTS.CART.COUNT);
      return response.data;
    } catch (error) {
      console.error('Get cart count API Error:', error.message);
      return {
        success: false,
        count: 0,
        message: error.response?.data?.message || 'Failed to fetch cart count',
      };
    }
  }

  async updateCartItem(bookId, quantity) {
    try {
      const response = await apiClient.post(API_CONFIG.ENDPOINTS.CART.UPDATE, {
        bookId: bookId,
        itemId: bookId, // Support both parameter names
        quantity,
      });
      return response.data;
    } catch (error) {
      console.error('Update cart item API Error:', error.message);
      if (error.response) {
        const data = error.response.data || {};
        return {
          success: false,
          message: data.message || 'Failed to update cart',
          code: data.code,
          bookType: data.bookType,
        };
      } else if (error.request) {
        return {
          success: false,
          message: 'Cannot connect to server. Make sure backend is running.',
        };
      } else {
        return {
          success: false,
          message: 'Failed to update cart. Please try again.',
        };
      }
    }
  }

  /**
   * Add multiple items to cart in one request (replaces cart). Use for fast add-from-grade.
   * @param {Array<{ itemId: string, quantity: number }>} items
   * @returns {Promise<{ success, data?, addedCount?, message? }>}
   */
  async addItemsToCart(items) {
    try {
      const response = await apiClient.post(API_CONFIG.ENDPOINTS.CART.ADD_ITEMS, { items });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getCart() {
    try {
      const response = await apiClient.get(API_CONFIG.ENDPOINTS.CART.GET);
      return response.data;
    } catch (error) {
      console.error('Get cart API Error:', error.message);
      if (error.response) {
        return {
          success: false,
          message: error.response.data?.message || 'Failed to fetch cart',
          data: { items: [] },
        };
      } else if (error.request) {
        return {
          success: false,
          message: 'Cannot connect to server. Make sure backend is running.',
          data: { items: [] },
        };
      } else {
        return {
          success: false,
          message: 'Failed to fetch cart. Please try again.',
          data: { items: [] },
        };
      }
    }
  }

  async removeCartItem(itemId) {
    try {
      const response = await apiClient.delete(`${API_CONFIG.ENDPOINTS.CART.REMOVE}/${itemId}`);
      return response.data;
    } catch (error) {
      console.error('Remove cart item API Error:', error.message);
      if (error.response) {
        return {
          success: false,
          message: error.response.data?.message || 'Failed to remove item from cart',
        };
      } else if (error.request) {
        return {
          success: false,
          message: 'Cannot connect to server. Make sure backend is running.',
        };
      } else {
        return {
          success: false,
          message: 'Failed to remove item. Please try again.',
        };
      }
    }
  }

  async clearCart() {
    try {
      const response = await apiClient.post(API_CONFIG.ENDPOINTS.CART.CLEAR);
      return response.data;
    } catch (error) {
      console.error('Clear cart API Error:', error.message);
      if (error.response) {
        return {
          success: false,
          message: error.response.data?.message || 'Failed to clear cart',
        };
      } else if (error.request) {
        return {
          success: false,
          message: 'Cannot connect to server. Make sure backend is running.',
        };
      } else {
        return {
          success: false,
          message: 'Failed to clear cart. Please try again.',
        };
      }
    }
  }

  // Payment APIs
  async createPaymentOrder(amount, receipt, orderingStudent = null, cartSnapshot = null, shippingAddress = null) {
    try {
      const body = { amount, receipt };
      if (orderingStudent && orderingStudent.name) {
        body.orderingStudent = orderingStudent;
      }
      if (Array.isArray(cartSnapshot) && cartSnapshot.length > 0) {
        body.cartSnapshot = cartSnapshot;
      }
      if (shippingAddress && typeof shippingAddress === 'object') {
        body.shippingAddress = shippingAddress;
      }
      const response = await apiClient.post(API_CONFIG.ENDPOINTS.PAYMENT.CREATE_ORDER, body, {
        timeout: API_CONFIG.CHECKOUT_TIMEOUT,
      });
      return response.data;
    } catch (error) {
      console.error('Create payment order API Error:', error.message);
      if (error.response) {
        return {
          success: false,
          message: error.response.data?.message || 'Failed to create payment order',
        };
      } else if (error.request) {
        return {
          success: false,
          message: 'Cannot connect to server. Make sure backend is running.',
        };
      } else {
        return {
          success: false,
          message: 'Failed to create payment order. Please try again.',
        };
      }
    }
  }

  async verifyPayment(orderId, paymentId, signature) {
    try {
      const response = await apiClient.post(API_CONFIG.ENDPOINTS.PAYMENT.VERIFY_PAYMENT, {
        orderId,
        paymentId,
        signature,
      }, {
        timeout: API_CONFIG.CHECKOUT_TIMEOUT,
      });
      return response.data;
    } catch (error) {
      console.error('Verify payment API Error:', error.message);
      if (error.response) {
        return {
          success: false,
          message: error.response.data?.message || 'Failed to verify payment',
        };
      } else if (error.request) {
        return {
          success: false,
          message: 'Cannot connect to server. Make sure backend is running.',
        };
      } else {
        return {
          success: false,
          message: 'Failed to verify payment. Please try again.',
        };
      }
    }
  }

  // Orders APIs
  async getOrders() {
    try {
      const response = await apiClient.get(API_CONFIG.ENDPOINTS.ORDERS.GET_ALL);
      
      // Handle Firebase order response structure
      if (response.data) {
        // If response.data already has success and data properties
        if (response.data.success !== undefined && response.data.data) {
          return {
            success: response.data.success,
            data: response.data.data || [],
          };
        }
        // If response.data is directly the array
        if (Array.isArray(response.data)) {
          return {
            success: true,
            data: response.data,
          };
        }
      }
      
      return {
        success: true,
        data: [],
      };
    } catch (error) {
      console.error('Get orders API Error:', error.message);
      if (error.response) {
        return {
          success: false,
          message: error.response.data?.message || 'Failed to fetch orders',
          data: [],
        };
      } else if (error.request) {
        return {
          success: false,
          message: 'Cannot connect to server. Make sure backend is running.',
          data: [],
        };
      } else {
        return {
          success: false,
          message: 'Failed to fetch orders. Please try again.',
          data: [],
        };
      }
    }
  }

  async downloadInvoice(orderId, orderNumber) {
    try {
      const response = await apiClient.get(`${API_CONFIG.ENDPOINTS.ORDERS.GET_BY_ID}/${orderId}/invoice`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Invoice_${String(orderNumber || orderId).replace(/[^\w-]+/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      return { success: true };
    } catch (error) {
      console.error('Download invoice API Error:', error.message);
      return { success: false, message: 'Could not download invoice. Please try again.' };
    }
  }

  async getOrderById(orderId) {
    try {
      const response = await apiClient.get(`${API_CONFIG.ENDPOINTS.ORDERS.GET_BY_ID}/${orderId}`);
      if (response.data && response.data.success !== undefined) {
        return response.data;
      }
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Get order API Error:', error.message);
      if (error.response) {
        return {
          success: false,
          message: error.response.data?.message || 'Failed to fetch order',
          data: null,
        };
      } else if (error.request) {
        return {
          success: false,
          message: 'Cannot connect to server. Make sure backend is running.',
          data: null,
        };
      } else {
        return {
          success: false,
          message: 'Failed to fetch order. Please try again.',
          data: null,
        };
      }
    }
  }

  /**
   * Validate cart for checkout (inventory check only). Call before opening payment.
   * On INSUFFICIENT_STOCK: mandatory = whole grade blocked; optional = user should uncheck those bundles.
   */
  async validateCartForCheckout() {
    try {
      const response = await apiClient.post(API_CONFIG.ENDPOINTS.ORDERS.VALIDATE_CART, {}, {
        timeout: API_CONFIG.CHECKOUT_TIMEOUT,
      });
      return response.data;
    } catch (error) {
      console.error('Validate cart API Error:', error.message);
      if (error.response && error.response.status === 400) {
        const data = error.response.data || {};
        return {
          success: false,
          valid: false,
          message: data.message || 'Cart cannot be fulfilled',
          code: data.code,
          insufficientBundles: data.insufficientBundles || null,
        };
      }
      if (error.response) {
        return {
          success: false,
          valid: false,
          message: error.response.data?.message || 'Failed to validate cart',
        };
      }
      if (error.request) {
        const isTimeout = error.code === 'ECONNABORTED' || error.message?.includes('timeout');
        return {
          success: false,
          valid: false,
          message: isTimeout
            ? 'Request timed out. The server is taking too long to validate your cart. Please try again.'
            : 'Cannot connect to server. Make sure backend is running.',
        };
      }
      return {
        success: false,
        valid: false,
        message: 'Failed to validate cart. Please try again.',
      };
    }
  }

  async createOrder(paymentData, shippingAddress = null, orderingStudent = null) {
    try {
      const body = { paymentData, shippingAddress };
      if (orderingStudent && orderingStudent.name) {
        body.orderingStudent = orderingStudent;
      }
      const response = await apiClient.post(API_CONFIG.ENDPOINTS.ORDERS.CREATE, body, {
        timeout: API_CONFIG.CHECKOUT_TIMEOUT,
      });
      return response.data;
    } catch (error) {
      console.error('Create order API Error:', error.message);
      if (error.response) {
        const data = error.response.data || {};
        return {
          success: false,
          message: data.message || 'Failed to create order',
          code: data.code,
          insufficientBundles: data.insufficientBundles || null,
        };
      }
      if (error.request) {
        return {
          success: false,
          message: 'Cannot connect to server. Make sure backend is running.',
        };
      }
      return {
        success: false,
        message: 'Failed to create order. Please try again.',
      };
    }
  }

  // User APIs
  async getUserById(userId) {
    try {
      const response = await apiClient.get(`${API_CONFIG.ENDPOINTS.USERS.GET_BY_ID}/${userId}`);
      return response.data;
    } catch (error) {
      console.error('Get user by ID API Error:', error.message);
      if (error.response) {
        return {
          success: false,
          message: error.response.data?.message || 'Failed to fetch user',
          data: null,
        };
      } else if (error.request) {
        return {
          success: false,
          message: 'Cannot connect to server. Make sure backend is running.',
          data: null,
        };
      } else {
        return {
          success: false,
          message: 'Failed to fetch user. Please try again.',
          data: null,
        };
      }
    }
  }

  async updateUserProfile(userId, updateData) {
    try {
      const response = await apiClient.put(`${API_CONFIG.ENDPOINTS.USERS.UPDATE}/${userId}`, updateData);
      const result = response.data;
      if (result?.success && result.data) {
        this.storeUserData(result.data);
      }
      return result;
    } catch (error) {
      console.error('Update user profile API Error:', error.message);
      if (error.response) {
        return {
          success: false,
          message: error.response.data?.message || 'Failed to update profile',
        };
      } else if (error.request) {
        return {
          success: false,
          message: 'Cannot connect to server. Make sure backend is running.',
        };
      } else {
        return {
          success: false,
          message: 'Failed to update profile. Please try again.',
        };
      }
    }
  }

  // Upload image (using admin panel backend - different base URL)
  async uploadImage(file, metadata = {}) {
    try {
      // Create FormData for file upload
      const formData = new FormData();
      
      // Append the file
      formData.append('file', file);
      
      // Append metadata
      if (metadata.category) formData.append('category', metadata.category);
      if (metadata.description) formData.append('description', metadata.description);
      if (metadata.folderPath) formData.append('folderPath', metadata.folderPath);
      if (metadata.uploadedBy) formData.append('uploadedBy', metadata.uploadedBy);

      // Use axios directly (not apiClient) since this endpoint is on a different base URL
      const response = await axios.post(API_CONFIG.ENDPOINTS.UPLOAD.IMAGE, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response.data;
    } catch (error) {
      console.error('Upload image API Error:', error.message);
      console.error('Upload error details:', error.response?.data);
      if (error.response) {
        return {
          success: false,
          message: error.response.data?.message || 'Failed to upload image',
          url: null,
        };
      } else if (error.request) {
        return {
          success: false,
          message: 'Cannot connect to server. Make sure backend is running.',
          url: null,
        };
      } else {
        return {
          success: false,
          message: 'Failed to upload image. Please try again.',
          url: null,
        };
      }
    }
  }

  setSessionExpiredHandler(handler) {
    sessionExpiredHandler = handler;
  }

  /**
   * On site load: renew the session token, or (browsers logged in before tokens existed)
   * swap the stored user id for one — the server allows that swap only during the rollout month.
   * @returns {Promise<'ok'|'expired'|'unknown'>} 'expired' = must log in again; 'unknown' = offline/server issue
   */
  async ensureSession() {
    const token = localStorage.getItem('authToken');
    const isRealToken = !!token && token.split('.').length === 3;
    try {
      const response = await apiClient.post(
        isRealToken ? API_CONFIG.ENDPOINTS.AUTH.REFRESH : API_CONFIG.ENDPOINTS.AUTH.TOKEN_EXCHANGE
      );
      if (response.data?.token) {
        localStorage.setItem('authToken', response.data.token);
        return 'ok';
      }
      return 'unknown';
    } catch (error) {
      return isSessionExpiredError(error) ? 'expired' : 'unknown';
    }
  }

  hasSessionToken() {
    try {
      const token = localStorage.getItem('authToken');
      return !!token && token.split('.').length === 3;
    } catch (e) {
      return false;
    }
  }

  // Staff "School Orders" (read-only)
  async getStaffAccess() {
    const response = await apiClient.get(API_CONFIG.ENDPOINTS.STAFF.ME);
    return response.data;
  }

  /** School Orders date filter: { from?: 'YYYY-MM-DD', to?: 'YYYY-MM-DD' } → query params (empty values dropped). */
  staffRangeParams(range = {}) {
    const params = {};
    if (range.from) params.from = range.from;
    if (range.to) params.to = range.to;
    return params;
  }

  async getStaffSchools(range = {}) {
    const response = await apiClient.get(API_CONFIG.ENDPOINTS.STAFF.SCHOOLS, { params: this.staffRangeParams(range) });
    return response.data;
  }

  async getStaffSchoolClasses(schoolId, range = {}) {
    const response = await apiClient.get(
      `${API_CONFIG.ENDPOINTS.STAFF.SCHOOLS}/${encodeURIComponent(schoolId)}/classes`,
      { params: this.staffRangeParams(range) }
    );
    return response.data;
  }

  /** @param {string|null} sectionId subgrade id, 'none' for books without a section, null for whole class */
  async getStaffClassOrders(schoolId, gradeId, sectionId = null, range = {}) {
    const response = await apiClient.get(
      `${API_CONFIG.ENDPOINTS.STAFF.SCHOOLS}/${encodeURIComponent(schoolId)}/classes/${encodeURIComponent(gradeId)}/orders`,
      { params: { ...(sectionId ? { sectionId } : {}), ...this.staffRangeParams(range) } }
    );
    return response.data;
  }

  // Storage helpers
  storeUserData(user) {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        if (user.id) {
          localStorage.setItem('userId', user.id.toString());
        }
        localStorage.setItem('userData', JSON.stringify(user));
        localStorage.setItem('isLoggedIn', 'true');
      }
    } catch (error) {
      console.log('Error storing user data:', error);
    }
  }

  getUserData() {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        const userData = localStorage.getItem('userData');
        if (userData) {
          return JSON.parse(userData);
        }
      }
      return null;
    } catch (error) {
      console.log('Error getting user data:', error);
      return null;
    }
  }
}

// Export singleton instance
export default new ApiService();

